import React, { useCallback, useRef } from "react";

import {
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { Box, Fab, InputAdornment, TextField, Typography } from "@mui/material";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import { useFormContext } from "react-hook-form";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

import { RecipeFormValues } from "./RecipeForm";

interface AddProps {
  text: string;
  onChange: (value: string) => void;
  onAdd: () => void;
}

interface EditProps {
  index: number;
  onMove: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  bodyText4: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.43,
    color: colors.black,
  },
  stepNumber: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 55,
    width: 55,
    border: "1px solid #DEE3EA",
  },
  remove: {
    border: `1px solid ${theme.palette.error.main}`,
    marginTop: 8,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.error.main,
  },
  dragIndicator: {
    cursor: "move",
  },
  dragging: {
    opacity: 0,
  },
}));

interface DragItem {
  index: number;
  id: string;
  type: string;
}

class CookingMethodInstruction {
  static DRAG_ITEM_KEY = "COOKING_METHOD_INSTRUCTION";

  static Add: React.FC<AddProps> = ({ text, onChange, onAdd }) => {
    const { classes } = useStyles();

    const addInstruction = useCallback(() => {
      onAdd();
      onChange("");
    }, [onAdd, onChange]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange]
    );

    const handleKeyPress = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          addInstruction();
        }
      },
      [addInstruction]
    );

    return (
      <Box display="flex" alignItems="center">
        <Box className={classes.stepNumber}>
          <Typography className={classes.bodyText4}>No</Typography>
        </Box>
        <Box flex={1} px={4}>
          <TextField
            placeholder="Write cooking instructions"
            value={text}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            fullWidth
          />
        </Box>
        <Fab
          color="primary"
          size="small"
          disabled={text === ""}
          onClick={addInstruction}
        >
          <AddIcon />
        </Fab>
      </Box>
    );
  };

  static Edit: React.FC<EditProps> = ({ index, onMove, onRemove }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { classes, cx } = useStyles();
    const [{ handlerId }, drop] = useDrop({
      accept: CookingMethodInstruction.DRAG_ITEM_KEY,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item, monitor) {
        if (!ref.current) return;

        const dragIndex = (item as DragItem).index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) return;

        // Determine rectangle on screen
        const hoverBoundingRect = ref.current?.getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        // Only perform the move when the mouse hsa crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        // Time to actually perform the action
        onMove(dragIndex, hoverIndex);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        (item as DragItem).index = hoverIndex;
      },
    });
    const { register } = useFormContext<RecipeFormValues>();

    const [{ isDragging }, drag] = useDrag({
      type: CookingMethodInstruction.DRAG_ITEM_KEY,
      item: () => {
        return { id: index, index };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(drop(ref));

    return (
      <div
        ref={ref}
        className={cx(classes.root, { [classes.dragging]: isDragging })}
        data-handler-id={handlerId}
      >
        <Box className={classes.stepNumber}>
          <Typography className={classes.bodyText4}>{index + 1}</Typography>
        </Box>
        <Box flex={1} px={4}>
          <TextField
            {...register(`instructions.${index}.text` as const)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <div className={classes.dragIndicator}>
                    <DragIndicatorIcon htmlColor="#C8CED8" />
                  </div>
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Box>
        <Fab
          className={classes.remove}
          size="small"
          onClick={() => onRemove(index)}
        >
          <RemoveIcon />
        </Fab>
      </div>
    );
  };
}

export default CookingMethodInstruction;
